/**
 * Tradução de erros de API para mensagem que o usuário pode ler.
 *
 * O problema que isto resolve: a API (NestJS + class-validator) devolve, num 400,
 * `response.data.message` como um array de strings técnicas em inglês, tipo
 * `["price must not be less than 0", "price must be a number conforming to the
 * specified constraints"]`. Quando isso cai direto num toast (`msg || 'fallback'`),
 * o array vira `"price must not be less than 0, price must be a number..."` na
 * tela. Nocivo e sem sentido para quem usa o app.
 *
 * Regra geral: erro de API só chega ao usuário se for uma frase que NÓS
 * escrevemos (um `BadRequestException('Escolha o estado antes da cidade.')`).
 * Ruído de validação, stack trace, `HTTP 500`, "Network Error" e afins viram o
 * fallback de domínio que a tela passou.
 */

/** Cara de mensagem técnica que NUNCA deve aparecer em toast. */
export const TECHNICAL_ERROR_RE =
  /(must be|must not be|should not|conforming to the specified|is not a valid|property [\w.]+ should not exist|each value in|Cannot read propert|is not defined|undefined is not|NaN|\bnull\b|\bundefined\b|Request failed with status|Network Error|timeout of \d+ms|ECONNREFUSED|ETIMEDOUT|\bAxiosError\b|<!DOCTYPE|Internal server error|Unexpected token)/i;

/**
 * Nome de fornecedor/integração, variável de ambiente, rota ou frase em inglês.
 * Quem usa o app não sabe (nem precisa saber) o que é Evolution ou Stripe.
 */
const VENDOR_ERROR_RE =
  /\b(evolution|stripe|supabase|resend|twilio|firebase|onesignal|sentry|expo|typeorm|postgres(ql)?|sql|jwt|api|webhook|endpoint|payload|header|bundle id|product id|uid|instância|instance)\b/i;
const CODE_ERROR_RE =
  /([A-Z][A-Z0-9]+_[A-Z0-9_]+|\.env\b|\b(GET|POST|PUT|PATCH|DELETE)\s+\/|\b[a-z]+Id\b)/;
const ENGLISH_ERROR_RE =
  /\b(the|is|are|does|do not|not found|cannot|can only|only|must|should|missing|invalid|required|already|user|provider|forbidden|unauthorized|bad request|internal)\b/i;

export function isTechnicalErrorText(text: string): boolean {
  return (
    TECHNICAL_ERROR_RE.test(text) ||
    VENDOR_ERROR_RE.test(text) ||
    CODE_ERROR_RE.test(text) ||
    ENGLISH_ERROR_RE.test(text)
  );
}

const GENERIC_ERROR = 'Algo deu errado. Tente novamente.';
const NETWORK_ERROR = 'Sem conexão. Verifique sua internet e tente de novo.';

/**
 * Limpa um texto solto antes de virar toast. Devolve `fallback` quando o texto
 * está vazio, não é string, ou tem cara de mensagem técnica.
 */
export function sanitizeErrorText(text: unknown, fallback = GENERIC_ERROR): string {
  if (typeof text !== 'string') return fallback;
  const trimmed = text.trim();
  if (!trimmed) return fallback;
  if (trimmed.length > 200) return fallback;
  if (isTechnicalErrorText(trimmed)) return fallback;
  return trimmed;
}

type ApiErrorShape = {
  isAxiosError?: boolean;
  code?: string;
  response?: {
    status?: number;
    data?: { message?: unknown; error?: unknown; code?: unknown };
  };
  message?: unknown;
};

/**
 * Erros com `code` que o app sabe explicar melhor que a frase da API. Os de
 * plano normalmente abrem o `PlanLimitModal`; a frase aqui é para quem cai no
 * toast (fluxo sem o modal, ou dentro de um Modal que o esconderia).
 */
const KNOWN_ERROR_CODES: Record<string, string> = {
  PLAN_SEATS_EXCEEDED:
    'O plano da equipe não cobre mais profissionais. Ajuste o plano para continuar.',
  PLAN_LIMIT_EXCEEDED: 'Seu plano chegou ao limite de clientes deste mês.',
  ORGANIZATION_NOT_TEAM: 'Isso é só para contas com equipe. Dá para transformar a sua pelo Perfil.',
  MEMBER_DISABLED: 'Você não faz mais parte desta equipe.',
  PLAN_FEATURE_LOCKED: 'Esse recurso faz parte dos planos pagos.',
};

/** `code` da API, venha ele solto no corpo ou aninhado em `message`. */
export function getApiErrorCode(error: unknown): string | null {
  const data = (error as ApiErrorShape | null)?.response?.data;
  if (!data) return null;
  const nested = data.message && typeof data.message === 'object' ? data.message : null;
  const code = (nested as { code?: unknown } | null)?.code ?? data.code;
  return typeof code === 'string' ? code : null;
}

const NETWORK_MESSAGE_RE = /network|timeout|ECONN|ETIMEDOUT|ENOTFOUND|failed to fetch|internet/i;

/**
 * Mensagem pronta para mostrar ao usuário a partir de um erro de request.
 *
 * @param error    o erro cru (axios, Error, o que vier do catch)
 * @param fallback  mensagem de domínio da tela ("Não foi possível salvar o serviço.")
 */
export function getApiErrorMessage(error: unknown, fallback = GENERIC_ERROR): string {
  const err = (error ?? {}) as ApiErrorShape;
  const status = err.response?.status;

  // Sem resposta num erro de request (axios) = rede/offline/timeout.
  const msg = typeof err.message === 'string' ? err.message : '';
  if (
    err.response === undefined &&
    (err.isAxiosError === true || (err.code ?? '') !== '' || NETWORK_MESSAGE_RE.test(msg))
  ) {
    return NETWORK_ERROR;
  }

  // 5xx e 429: problema nosso ou de carga. Usuário não resolve lendo o detalhe.
  if (typeof status === 'number' && (status >= 500 || status === 429)) {
    return fallback;
  }

  // Error nosso, sem resposta de API (ex.: `throw new Error('...')` numa tela):
  // passa se for frase nossa; texto técnico de runtime vira fallback.
  if (err.response === undefined) return sanitizeErrorText(msg, fallback);

  const known = KNOWN_ERROR_CODES[getApiErrorCode(error) ?? ''];
  if (known) return known;

  const raw = err.response?.data?.message;

  // Array de class-validator: sempre ruído. Vai pro fallback.
  if (Array.isArray(raw)) return fallback;

  // Objeto (payload estruturado com `code`): quem sabe tratar trata; aqui, fallback.
  if (raw && typeof raw === 'object') {
    const nested = (raw as { message?: unknown }).message;
    return typeof nested === 'string' ? sanitizeErrorText(nested, fallback) : fallback;
  }

  // String: só passa se parecer frase escrita por nós.
  if (typeof raw === 'string') return sanitizeErrorText(raw, fallback);

  return fallback;
}

/**
 * Mesma regra de `getApiErrorMessage` para request feito com `fetch` direto
 * (upload multipart), que não passa pelo interceptor do axios.
 */
export function getFetchErrorMessage(status: number, data: unknown, fallback: string): string {
  const body = data && typeof data === 'object' ? (data as { message?: unknown }) : {};
  const shape: ApiErrorShape = { response: { status, data: body } };
  return getApiErrorMessage(shape, fallback);
}

/**
 * Erro do SDK do Stripe (web ou nativo). Só recusa de cartão e dado de cartão
 * inválido são escritos para o pagador (e vêm em pt-BR); o resto é detalhe de
 * integração e vira o fallback.
 */
export function getPaymentErrorMessage(
  error: { type?: string; message?: string } | null | undefined,
  fallback: string,
): string {
  if (error?.type === 'card_error' || error?.type === 'validation_error') {
    return sanitizeErrorText(error.message, fallback);
  }
  return fallback;
}
